import { useEffect, useMemo, useState } from 'react';
import { Star } from 'lucide-react';
import { PRODUCT, SAMPLE_REVIEWS } from '../data/product';
import { useCart } from '../context/CartContext';
import { useNavigate } from 'react-router-dom';

export default function ProductPage() {
  const [selectedColor, setSelectedColor] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [slideIndex, setSlideIndex] = useState(0);
  const [showAllDetails, setShowAllDetails] = useState(false);
  const { addItem } = useCart();
  const navigate = useNavigate();

  const selectedVariant = PRODUCT.colors[selectedColor];
  const totalPrice = useMemo(() => PRODUCT.price * quantity, [quantity]);
  const savingsAmount = useMemo(() => PRODUCT.mrp - PRODUCT.price, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setSlideIndex((prev) => (prev + 1) % PRODUCT.sliderImages.length);
    }, 2600);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    document.body.classList.add('route-product');
    return () => document.body.classList.remove('route-product');
  }, []);

  const onAddToCart = () => {
    addItem({
      colorName: selectedVariant.name,
      image: selectedVariant.image,
      quantity
    });
  };

  const onBuyNow = () => {
    onAddToCart();
    navigate('/cart');
  };

  return (
    <div className="product-page">
      <section id="product" className="product-hero">
        <div className="hero-media">
          <div className="hero-main-image">
            <img src={selectedVariant.image} alt={`${selectedVariant.name} Divoom Ditoo-Pro`} />
          </div>

          <div className="color-thumbs">
            {PRODUCT.colors.map((item, index) => (
              <button
                key={item.name}
                type="button"
                className={`color-thumb ${selectedColor === index ? 'active' : ''}`}
                onClick={() => setSelectedColor(index)}
              >
                <img src={item.image} alt={item.name} />
              </button>
            ))}
          </div>
        </div>

        <div className="hero-details">
          <p className="product-kicker">Retro Pixel Bluetooth Speaker</p>
          <h1>{PRODUCT.name}</h1>

          <div className="review-line">
            <div className="stars">
              {Array.from({ length: 5 }).map((_, index) => (
                <Star key={index} size={14} fill="currentColor" />
              ))}
            </div>
            <span>{PRODUCT.reviewsCount} Reviews</span>
          </div>

          <div className="pricing-summary">
            <div className="price-row">
              <p className="current-price">₹ {PRODUCT.price.toLocaleString('en-IN')}.00 INR</p>
              <p className="mrp-price">₹ {PRODUCT.mrp.toLocaleString('en-IN')} INR (mrp)</p>
            </div>
            <div className="price-meta">
              <span className="price-chip save-chip">
                Save ₹{' '}
                {savingsAmount.toLocaleString('en-IN', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                })}
              </span>
              <span className="price-chip">Tax included</span>
              <span className="price-chip">Prepaid only</span>
            </div>
          </div>

          <section className="desc-block" aria-label="About this product">
            <div className="desc-head">
              <h2>About this product</h2>
              <button
                type="button"
                className="desc-toggle"
                onClick={() => setShowAllDetails((prev) => !prev)}
                aria-expanded={showAllDetails}
                aria-controls="product-description-list"
              >
                {showAllDetails ? 'Show less' : 'Read full details'}
              </button>
            </div>

            <ul
              id="product-description-list"
              className={`desc-list ${showAllDetails ? 'expanded' : 'compact'}`}
            >
              {PRODUCT.descriptionPoints.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </section>

          <p className="note-line">
            <strong>Note:</strong> {PRODUCT.note}
          </p>

          <div className="color-selector">
            <h3>Available colours</h3>
            <div className="color-grid">
              {PRODUCT.colors.map((item, index) => (
                <button
                  key={item.name}
                  type="button"
                  className={`color-card ${selectedColor === index ? 'active' : ''}`}
                  onClick={() => setSelectedColor(index)}
                >
                  <img src={item.image} alt={item.name} />
                  <span>{item.name}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="trust-row">
            {PRODUCT.trustItems.map((item) => (
              <div key={item} className="trust-card">
                <span>{item}</span>
              </div>
            ))}
          </div>

          <div className="purchase-row">
            <div className="qty-control">
              <button type="button" onClick={() => setQuantity((prev) => Math.max(1, prev - 1))}>
                -
              </button>
              <span>{quantity}</span>
              <button type="button" onClick={() => setQuantity((prev) => Math.min(5, prev + 1))}>
                +
              </button>
            </div>
            <button type="button" className="buy-btn" onClick={onBuyNow}>
              Buy Now
            </button>
            <button type="button" className="add-btn" onClick={onAddToCart}>
              Add to Cart
            </button>
          </div>

          <p className="hero-total">
            Total: <strong>₹ {totalPrice.toLocaleString('en-IN')}.00 INR</strong>
          </p>
        </div>
      </section>

      <div className="mobile-purchase-dock" aria-label="Mobile quick purchase bar">
        <div className="mobile-purchase-price">
          <span>Total</span>
          <strong>₹ {totalPrice.toLocaleString('en-IN')}.00</strong>
        </div>
        <div className="mobile-purchase-actions">
          <div className="qty-control">
            <button type="button" onClick={() => setQuantity((prev) => Math.max(1, prev - 1))}>
              -
            </button>
            <span>{quantity}</span>
            <button type="button" onClick={() => setQuantity((prev) => Math.min(5, prev + 1))}>
              +
            </button>
          </div>
          <button type="button" className="add-btn" onClick={onAddToCart}>
            Add
          </button>
          <button type="button" className="buy-btn" onClick={onBuyNow}>
            Buy
          </button>
        </div>
      </div>

      <section className="full-image-wrap" aria-label="Below hero section image">
        <img src={PRODUCT.belowHeroImage} alt="Ditoo-Pro showcase" />
      </section>

      <section id="showcase" className="slider-section">
        <h2>Product Showcase</h2>
        <div className="slider-frame">
          <img src={PRODUCT.sliderImages[slideIndex]} alt={`Product slide ${slideIndex + 1}`} />
        </div>
        <div className="slider-dots">
          {PRODUCT.sliderImages.map((image, index) => (
            <button
              key={image}
              type="button"
              className={slideIndex === index ? 'active' : ''}
              onClick={() => setSlideIndex(index)}
              aria-label={`Show slide ${index + 1}`}
            />
          ))}
        </div>
      </section>

      <section className="full-image-wrap bigger-display" aria-label="Bigger product display">
        <img src={PRODUCT.biggerImage} alt="Bigger product display" />
      </section>

      <section className="video-section" aria-label="Ditoo-Pro video demo">
        <h2>Watch Ditoo-Pro in Action</h2>
        <div className="video-frame">
          <iframe
            src={PRODUCT.videoEmbed}
            title="Divoom Ditoo-Pro video"
            frameBorder="0"
            allow="autoplay; encrypted-media; picture-in-picture"
            allowFullScreen
          />
        </div>
      </section>

      <section className="full-image-wrap footer-image-wrap" aria-label="Footer promo image">
        <img src={PRODUCT.footerImage} alt="Ditoo-Pro footer showcase" />
      </section>

      <section id="reviews" className="reviews-wrap">
        <h2>Customer Reviews</h2>
        <div className="review-grid">
          {SAMPLE_REVIEWS.map((item) => (
            <article key={item.name}>
              <div className="review-stars">★★★★★</div>
              <h3>{item.title}</h3>
              <p>{item.body}</p>
              <span>{item.name}</span>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}
